import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import LeadsPage from "./pages/LeadsPage";
import LeadDetailPage from "./pages/LeadDetailPage";
import BusinessSearchPage from "./pages/BusinessSearchPage";
import ImportPage from "./pages/ImportPage";
import TeamPage from "./pages/TeamPage";
import TeamHierarchyPage from "./pages/TeamHierarchyPage";
import ReportsPage from "./pages/ReportsPage";
import BillingPage from "./pages/BillingPage";
import UsagePage from "./pages/UsagePage";
import TasksPage from "./pages/TasksPage";
import SalesGoalsPage from "./pages/SalesGoalsPage";
import SSOCallback from "./pages/SSOCallback";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import PlatformSettingsPage from "./pages/PlatformSettingsPage";
import SettingsPage from "./pages/SettingsPage";
import LearningCenterPage from "./pages/LearningCenterPage";
import CourseDetailPage from "./pages/CourseDetailPage";
import LessonPage from "./pages/LessonPage";
import LearningAdminPage from "./pages/LearningAdminPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ManagerOversightPage from "./pages/ManagerOversightPage";
import ChatPage from "./pages/ChatPage";
import SalesIntelligencePage from "./pages/SalesIntelligencePage";
import EmailTemplatesPage from "./pages/EmailTemplatesPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectBoardPage from "./pages/ProjectBoardPage";
import HelpDeskPage from "./pages/HelpDeskPage";
import CalendarPage from "./pages/CalendarPage";
import PublicBookingPage from "./pages/PublicBookingPage";
import ActivityFeedPage from "./pages/ActivityFeedPage";
import CallHistoryPage from "./pages/CallHistoryPage";
import LandingPage from "./pages/LandingPage";
import MailSettingsPage from "./pages/MailSettingsPage";
import CampaignsPage from "./pages/CampaignsPage";
import CampaignRepliesPage from "./pages/CampaignRepliesPage";
import SentEmailsPage from "./pages/SentEmailsPage";
import Layout from "./components/Layout";
import "./App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
      
      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
          credentials: "include"
        });

        if (response.ok) {
          const userData = await response.json();
          navigate("/dashboard", { state: { user: userData } });
        } else {
          navigate("/login");
        }
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isSessionCallback = location.hash?.includes("session_id=") || window.location.hash?.includes("session_id=");

  // If user data passed from AuthCallback, skip auth check
  useEffect(() => {
    if (isSessionCallback) return;

    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include"
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location.state, isSessionCallback]);

  // If there's a session_id in the hash, let AuthCallback handle it
  if (isSessionCallback) {
    return <AuthCallback />;
  }

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout user={user}>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  // This MUST run before any protected route checks
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Also check the pathname hash directly in case location.hash is stale
  if (window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Auth Routes */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/sso/callback" element={<SSOCallback />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/book/:slug" element={<PublicBookingPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
      <Route path="/leads/:leadId" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />
      <Route path="/business-search" element={<ProtectedRoute><BusinessSearchPage /></ProtectedRoute>} />
      <Route path="/import" element={<ProtectedRoute><ImportPage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/team/hierarchy" element={<ProtectedRoute><TeamHierarchyPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/billing/success" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/usage" element={<ProtectedRoute><UsagePage /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><SalesGoalsPage /></ProtectedRoute>} />
      <Route path="/platform-admin" element={<ProtectedRoute><PlatformAdminPage /></ProtectedRoute>} />
      <Route path="/platform-admin/settings" element={<ProtectedRoute><PlatformSettingsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      
      {/* Learning Center Routes */}
      <Route path="/learning" element={<ProtectedRoute><LearningCenterPage /></ProtectedRoute>} />
      <Route path="/learning/courses/:courseId" element={<ProtectedRoute><CourseDetailPage /></ProtectedRoute>} />
      <Route path="/learning/lessons/:lessonId" element={<ProtectedRoute><LessonPage /></ProtectedRoute>} />
      <Route path="/learning/admin" element={<ProtectedRoute><LearningAdminPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/manager-oversight" element={<ProtectedRoute><ManagerOversightPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/sales-intelligence" element={<ProtectedRoute><SalesIntelligencePage /></ProtectedRoute>} />
      <Route path="/email-templates" element={<ProtectedRoute><EmailTemplatesPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectBoardPage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpDeskPage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/activity" element={<ProtectedRoute><ActivityFeedPage /></ProtectedRoute>} />
      <Route path="/calls" element={<ProtectedRoute><CallHistoryPage /></ProtectedRoute>} />
      <Route path="/settings/mail" element={<ProtectedRoute><MailSettingsPage /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
      <Route path="/campaigns/replies" element={<ProtectedRoute><CampaignRepliesPage /></ProtectedRoute>} />
      <Route path="/sent-emails" element={<ProtectedRoute><SentEmailsPage /></ProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RootRoute() {
  const [authState, setAuthState] = useState("checking");

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => {
        if (!cancelled) setAuthState(r.ok ? "authed" : "guest");
      })
      .catch(() => {
        if (!cancelled) setAuthState("guest");
      });
    return () => { cancelled = true; };
  }, []);

  if (authState === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }
  if (authState === "authed") return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;
